import { CORES_DESTAQUE, ROTULO_COR, type CorDestaque } from "@horofacial/shared";
import { Check, Monitor, Moon, Sun } from "lucide-react";
import { clinicaTemCor, definirAparencia, useAparencia, type Aparencia } from "../lib/tema";
import { useAvisar } from "./ui";

const MODOS: { modo: Aparencia["modo"]; rotulo: string; Icone: typeof Sun }[] = [
  { modo: "sistema", rotulo: "Sistema", Icone: Monitor },
  { modo: "claro", rotulo: "Claro", Icone: Sun },
  { modo: "escuro", rotulo: "Escuro", Icone: Moon },
];

/** Amostra de cada cor (as mesmas de estilos.css); light-dark() mostra a versão do modo em uso. */
const AMOSTRA: Record<CorDestaque, string> = {
  clinica: "light-dark(var(--marca-clinica-claro, #2f5d50), var(--marca-clinica-escuro, #8cc3ad))",
  verde: "light-dark(#2f5d50, #8cc3ad)",
  azul: "light-dark(#1d5fae, #8ab8ff)",
  petroleo: "light-dark(#0f6b6b, #6fd0cf)",
  ameixa: "light-dark(#6b3fa0, #c4a5f0)",
  rosa: "light-dark(#a3305f, #f2a3c4)",
  terracota: "light-dark(#a4461a, #f5ad7f)",
  ouro: "light-dark(#7a5200, #e8c26a)",
  grafite: "light-dark(#44474f, #c4c7cf)",
};

const TONS: { tom: Aparencia["tom"]; rotulo: string; amostra: string }[] = [
  { tom: "marfim", rotulo: "Quente", amostra: "light-dark(#f2f0ec, #222225)" },
  { tom: "neutro", rotulo: "Neutro", amostra: "light-dark(#eff1f4, #1f2328)" },
];

/**
 * Aparência do usuário: modo, cor de destaque e tom do fundo. Vale na hora e fica guardada no servidor.
 * Alto contraste e cores forçadas do sistema (Windows) passam por cima pelo CSS, sem opção aqui.
 */
export function PainelAparencia() {
  const a = useAparencia();
  const avisar = useAvisar();
  const comClinica = clinicaTemCor();
  const cores = CORES_DESTAQUE.filter((c) => c !== "clinica" || comClinica);
  // Sem escolha, vale a cor da clínica (ou o verde, se ela não tiver uma).
  const corEmUso: CorDestaque = a.cor ?? (comClinica ? "clinica" : "verde");
  const mudar = (m: Partial<Aparencia>) => definirAparencia(m).catch(() => avisar("Não deu para guardar a aparência no servidor; ela vale só neste navegador.", "erro"));

  return (
    <div className="grid gap-4">
      <fieldset>
        <legend className="mb-2 text-sm font-medium text-texto-suave">Modo</legend>
        <div role="radiogroup" aria-label="Modo" className="segmentado grid grid-cols-3">
          {MODOS.map(({ modo, rotulo, Icone }) => (
            <button key={modo} type="button" role="radio" aria-checked={a.modo === modo} data-ativo={a.modo === modo || undefined} onClick={() => mudar({ modo })} className="flex min-h-10 items-center justify-center gap-1.5 text-sm">
              <Icone className="size-4" aria-hidden />
              {rotulo}
            </button>
          ))}
        </div>
      </fieldset>
      <fieldset>
        <legend className="mb-2 text-sm font-medium text-texto-suave">
          Cor de destaque: <span className="text-texto">{ROTULO_COR[corEmUso]}</span>
        </legend>
        <div role="radiogroup" aria-label="Cor de destaque" className="flex flex-wrap gap-2">
          {cores.map((c) => (
            <button
              key={c}
              type="button"
              role="radio"
              aria-checked={corEmUso === c}
              aria-label={ROTULO_COR[c]}
              title={ROTULO_COR[c]}
              onClick={() => mudar({ cor: c })}
              className={`grid size-10 place-items-center rounded-full border border-borda-forte ${corEmUso === c ? "ring-2 ring-texto ring-offset-2 ring-offset-superficie" : ""}`}
              style={{ background: AMOSTRA[c] }}
            >
              {corEmUso === c && <Check className="size-4 text-white mix-blend-difference" aria-hidden />}
            </button>
          ))}
        </div>
      </fieldset>
      <fieldset>
        <legend className="mb-2 text-sm font-medium text-texto-suave">Fundo</legend>
        <div role="radiogroup" aria-label="Fundo" className="segmentado grid grid-cols-2">
          {TONS.map(({ tom, rotulo, amostra }) => (
            <button key={tom} type="button" role="radio" aria-checked={a.tom === tom} data-ativo={a.tom === tom || undefined} onClick={() => mudar({ tom })} className="flex min-h-10 items-center justify-center gap-2 text-sm">
              <span className="size-4 rounded-full border border-borda-forte" style={{ background: amostra }} aria-hidden />
              {rotulo}
            </button>
          ))}
        </div>
      </fieldset>
    </div>
  );
}
